import { inject, Inject, InjectionToken, Optional } from '@angular/core';
import { Injectable } from '@angular/core';
import { TRACKING_SEGMENT_KEY } from '../../models';

@Injectable({
    providedIn: 'root',
})
export class SegmentLibraryLoader {
    private segmentKey = inject<string>(TRACKING_SEGMENT_KEY);
    constructor() {}

    injectSegmentLibrary(): void {
        if (this.segmentKey) {
            // call Segment provided inject code
            this.inject();
        }
    }

    /**
     * Loads the segment library provided at: https://segment.com/docs/sources/website/analytics.js/quickstart/#step-1-copy-the-snippet
     *
     * This chunk of code was copied from Segment library and should not be modified unless absolutely necessary
     *
     */
    private inject() {
        // Create a queue, but don't obliterate an existing one!
        const analyticsGlobal: any = (window['analytics'] = <any>(
            (window['analytics'] || [])
        ));

        // If the real analytics.js is already on the page return.
        if (analyticsGlobal.initialize) {
            return;
        }

        // If the snippet was invoked already show an error.
        if (analyticsGlobal.invoked) {
            if (window.console && console.error) {
                console.error('Segment snippet included twice.');
            }
            return;
        }

        // Invoked flag, to make sure the snippet
        // is never invoked twice.
        analyticsGlobal.invoked = true;

        // A list of the methods in Analytics.js to stub.
        analyticsGlobal.methods = [
            'trackSubmit',
            'trackClick',
            'trackLink',
            'trackForm',
            'pageview',
            'identify',
            'reset',
            'group',
            'track',
            'ready',
            'alias',
            'debug',
            'page',
            'once',
            'off',
            'on',
        ];

        // Define a factory to create stubs. These are placeholders
        // for methods in Analytics.js so that you never have to wait
        // for it to load to actually record data. The `method` is
        // stored as the first argument, so we can replay the data.
        analyticsGlobal.factory = function (method) {
            return function () {
                const args = Array.prototype.slice.call(arguments);
                args.unshift(method);
                analyticsGlobal.push(args);
                return analyticsGlobal;
            };
        };

        // For each of our methods, generate a queueing stub.
        for (let i = 0; i < analyticsGlobal.methods.length; i++) {
            const key = analyticsGlobal.methods[i];
            analyticsGlobal[key] = analyticsGlobal.factory(key);
        }

        // Define a method to load Analytics.js from our CDN,
        // and that will be sure to only ever load it once.
        analyticsGlobal.load = function (key, options) {
            // Create an async script element based on your key.
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.async = true;
            script.src =
                'https://cdn.segment.com/analytics.js/v1/' +
                key +
                '/analytics.min.js';

            // Insert our script next to the first script element.
            const first = document.getElementsByTagName('script')?.[0];
            first?.parentNode?.insertBefore(script, first);
            analyticsGlobal._loadOptions = options;
        };

        // Add a version to keep track of what's in the wild.
        analyticsGlobal.SNIPPET_VERSION = '4.1.0';

        // Load Analytics.js with your key, which will automatically
        // load the tools you've enabled for your account. Boosh!
        analyticsGlobal.load(this.segmentKey);
    }
}
